import React, { useCallback, useEffect, useRef, useState } from "react";

/* ────────────────────────────────────────────────────────────────────
   Checkpoint Charlie · Live Camera + Visual-Crypto Verifier
   Route: /cc/scan
   No app install · pure browser WebRTC · no manual capture button.

   Pipeline per frame:
     1. requestAnimationFrame sampler captures raw RGBA at ~10 fps.
     2. Every ~1.2 s a verifier tick crops the reticle area, encodes
        as PNG, and POSTs multipart to /api/cc/verify with the doc_id.
     3. Server returns {ok, revealed_text, artifact_png_b64, ...}.
     4. On ok=true  → VERIFIED state with the recovered secret.
        On ok=false → instant FRAUD DETECTED black banner overlaid
        across the reticle, plus the destructive-artifact PNG.

   Latency target: ≤ 300 ms from server response → banner painted.
   React batched state set + the banner sitting at z-index 30 above
   the video means a single browser repaint cycle. We measure and
   display the actual round-trip for diagnostics.

   Background auto-save: the verifier runs on its own setInterval so
   nothing in the UI thread blocks. Operator never taps anything once
   the camera is live.
   ──────────────────────────────────────────────────────────────────── */

const SAMPLE_INTERVAL_MS = 100;     // frame capture cadence
const VERIFY_INTERVAL_MS = 1200;    // verify tick cadence
const RETICLE_FRAC = 0.65;          // reticle covers 65% of min(vw,vh)
const RETICLE_COLOR = "#FFCC00";
const CORNER_THICKNESS = 6;
const CORNER_LENGTH = 36;

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function CheckpointCharlieScan() {
  const videoRef = useRef(null);
  const captureCanvasRef = useRef(null);
  const streamRef = useRef(null);
  const lastSampleRef = useRef(0);
  const rafRef = useRef(null);
  const verifyTimerRef = useRef(null);
  const verifyInFlightRef = useRef(false);
  const lastFrameDataRef = useRef(null); // most recent ImageData

  const [phase, setPhase] = useState("idle"); // idle | starting | live | error
  const [errorMsg, setErrorMsg] = useState("");
  const [frameCount, setFrameCount] = useState(0);

  // Doc-id is the lookup key for /api/cc/verify. Operator types it
  // before starting; live frames are continuously checked against this id.
  const [docId, setDocId] = useState("");

  // Verifier outcome state — fraud banner reads off these.
  const [verifyState, setVerifyState] = useState("waiting"); // waiting | checking | pass | fail | unknown
  const [verifyRevealed, setVerifyRevealed] = useState("");
  const [artifactPng, setArtifactPng] = useState("");
  const [latencyMs, setLatencyMs] = useState(0);
  const [verifyError, setVerifyError] = useState("");

  // ─── Frame sampler (per-frame, throttled) ─────────────────────
  const sample = useCallback(function sample() {
    const v = videoRef.current;
    const c = captureCanvasRef.current;
    if (!v || !c) {
      rafRef.current = requestAnimationFrame(sample);
      return;
    }
    const now = performance.now();
    if (now - lastSampleRef.current >= SAMPLE_INTERVAL_MS && v.readyState === 4) {
      lastSampleRef.current = now;
      c.width = v.videoWidth;
      c.height = v.videoHeight;
      const ctx = c.getContext("2d", { willReadFrequently: true });
      ctx.drawImage(v, 0, 0, c.width, c.height);
      lastFrameDataRef.current = ctx.getImageData(0, 0, c.width, c.height);
      setFrameCount((n) => n + 1);
    }
    rafRef.current = requestAnimationFrame(sample);
  }, []);

  // ─── Reticle crop ─────────────────────────────────────────────
  // Crops the centred square the operator was told to align with.
  // Returns a PNG Blob suitable for multipart upload, or null if the
  // capture canvas isn't ready.
  async function cropReticleToBlob() {
    const c = captureCanvasRef.current;
    if (!c || !c.width || !c.height) return null;
    const minDim = Math.min(c.width, c.height);
    const cropSize = Math.floor(minDim * RETICLE_FRAC);
    const x = Math.floor((c.width - cropSize) / 2);
    const y = Math.floor((c.height - cropSize) / 2);
    const out = document.createElement("canvas");
    out.width = cropSize;
    out.height = cropSize;
    const ctx = out.getContext("2d");
    ctx.drawImage(c, x, y, cropSize, cropSize, 0, 0, cropSize, cropSize);
    return new Promise((resolve) => out.toBlob(resolve, "image/png"));
  }

  const stopVerifyLoop = useCallback(() => {
    if (verifyTimerRef.current) {
      clearInterval(verifyTimerRef.current);
      verifyTimerRef.current = null;
    }
  }, []);

  // ─── Verify tick (background auto-save loop) ──────────────────
  const verifyTick = useCallback(async () => {
    if (!docId || verifyInFlightRef.current) return;
    const blob = await cropReticleToBlob();
    if (!blob) return;
    verifyInFlightRef.current = true;
    setVerifyState("checking");
    const t0 = performance.now();

    try {
      const fd = new FormData();
      fd.append("doc_id", docId);
      fd.append("frame", blob, "frame.png");

      const r = await fetch(`${API_BASE}/api/cc/verify`, {
        method: "POST",
        body: fd,
      });
      const latency = Math.round(performance.now() - t0);
      setLatencyMs(latency);

      if (r.status === 404) {
        setVerifyState("unknown");
        setVerifyError("Unknown document id");
        return;
      }
      if (!r.ok) {
        setVerifyState("unknown");
        setVerifyError(`server ${r.status}`);
        return;
      }
      const body = await r.json();
      if (body.ok) {
        setVerifyState("pass");
        setVerifyRevealed(body.revealed_text || "");
        setArtifactPng("");
        setVerifyError("");
        // Pass = lock the loop; operator can manually reset.
        stopVerifyLoop();
      } else {
        // FRAUD: instant black banner. setState is synchronous-enough
        // here — React batches and the next browser paint cycle
        // renders the banner well within our 300 ms budget.
        setVerifyState("fail");
        setArtifactPng(body.artifact_png_b64 || "");
        setVerifyRevealed("");
        setVerifyError("");
        stopVerifyLoop();
      }
    } catch (e) {
      setVerifyState("unknown");
      setVerifyError(e.message || "network error");
    } finally {
      verifyInFlightRef.current = false;
    }
  }, [docId, stopVerifyLoop]);

  const startVerifyLoop = useCallback(() => {
    if (verifyTimerRef.current) return;
    // Run one tick immediately, then on the interval.
    verifyTick();
    verifyTimerRef.current = setInterval(verifyTick, VERIFY_INTERVAL_MS);
  }, [verifyTick]);

  // ─── Camera lifecycle ─────────────────────────────────────────
  const startCamera = useCallback(async () => {
    if (!docId) {
      setErrorMsg("Enter a document ID before starting capture.");
      setPhase("error");
      return;
    }
    setPhase("starting");
    setErrorMsg("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });
      streamRef.current = stream;
      const v = videoRef.current;
      if (!v) return;
      v.srcObject = stream;
      await v.play();
      setPhase("live");
      setVerifyState("waiting");
      rafRef.current = requestAnimationFrame(sample);
      // Give the camera a moment to autofocus, then start the loop.
      setTimeout(startVerifyLoop, 600);
    } catch (e) {
      setPhase("error");
      setErrorMsg(`${e.name || "Error"}: ${e.message || "could not start camera"}`);
    }
  }, [docId, sample, startVerifyLoop]);

  const stopCamera = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    stopVerifyLoop();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setPhase("idle");
    setVerifyState("waiting");
    setVerifyRevealed("");
    setArtifactPng("");
  }, [stopVerifyLoop]);

  function resumeChecking() {
    setVerifyState("waiting");
    setArtifactPng("");
    setVerifyRevealed("");
    startVerifyLoop();
  }

  // Single-device demo path: verify an uploaded image directly. Skips
  // the camera + reticle pipeline. Useful for showing the FRAUD banner
  // on the same phone the operator browses the enterprise page on.
  async function verifyUploadedFile(file) {
    if (!docId) {
      setPhase("error");
      setErrorMsg("Enter a document ID first.");
      return;
    }
    if (!file) return;
    setPhase("live");
    setVerifyState("checking");
    const t0 = performance.now();
    try {
      const fd = new FormData();
      fd.append("doc_id", docId);
      fd.append("frame", file);
      const r = await fetch(`${API_BASE}/api/cc/verify`, { method: "POST", body: fd });
      setLatencyMs(Math.round(performance.now() - t0));
      if (r.status === 404) { setVerifyState("unknown"); setVerifyError("Unknown document id"); return; }
      const body = await r.json();
      if (body.ok) { setVerifyState("pass"); setVerifyRevealed(body.revealed_text || ""); setArtifactPng(""); }
      else { setVerifyState("fail"); setArtifactPng(body.artifact_png_b64 || ""); setVerifyRevealed(""); }
    } catch (e) {
      setVerifyState("unknown");
      setVerifyError(e.message || "Upload failed");
    }
  }

  useEffect(() => () => stopCamera(), [stopCamera]);

  return (
    <div className="fixed inset-0 bg-black text-white overflow-hidden flex flex-col">
      {/* Top status bar */}
      <div className="absolute top-0 inset-x-0 z-20 px-4 pt-3 pb-2 flex items-center justify-between text-[11px] tracking-widest font-mono uppercase pointer-events-none">
        <span className="opacity-80">CURB · CHECKPOINT</span>
        <span className={phase === "live" ? "text-[#FFCC00]" : "opacity-60"}>
          {phase === "live" && (
            <>
              ● LIVE · {frameCount}f
              {latencyMs ? ` · ${latencyMs}ms` : ""}
            </>
          )}
          {phase === "starting" && "● STARTING..."}
          {phase === "idle" && "○ STANDBY"}
          {phase === "error" && "● ERROR"}
        </span>
      </div>

      {/* Live video feed */}
      <video
        ref={videoRef}
        playsInline
        muted
        autoPlay
        className="absolute inset-0 w-full h-full object-cover"
        data-testid="cc-video"
      />

      {/* Hidden frame canvas */}
      <canvas ref={captureCanvasRef} className="hidden" />

      {/* Reticle */}
      {phase === "live" && verifyState !== "fail" && (
        <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
          <svg
            viewBox="0 0 200 200"
            style={{
              width: `min(${RETICLE_FRAC * 100}vw, ${RETICLE_FRAC * 100}vh)`,
              height: `min(${RETICLE_FRAC * 100}vw, ${RETICLE_FRAC * 100}vh)`,
            }}
            aria-hidden
          >
            <g
              stroke={verifyState === "pass" ? "#5fff8a" : RETICLE_COLOR}
              strokeWidth={CORNER_THICKNESS}
              fill="none"
              strokeLinecap="square"
            >
              <polyline points={`0,${CORNER_LENGTH} 0,0 ${CORNER_LENGTH},0`} />
              <polyline points={`${200 - CORNER_LENGTH},0 200,0 200,${CORNER_LENGTH}`} />
              <polyline points={`0,${200 - CORNER_LENGTH} 0,200 ${CORNER_LENGTH},200`} />
              <polyline points={`${200 - CORNER_LENGTH},200 200,200 200,${200 - CORNER_LENGTH}`} />
            </g>
            <rect
              x="2" y="2" width="196" height="196"
              fill="none"
              stroke={verifyState === "pass" ? "#5fff8a" : RETICLE_COLOR}
              strokeOpacity="0.25"
              strokeWidth="0.6"
            />
          </svg>
        </div>
      )}

      {/* Reticle label */}
      {phase === "live" && verifyState !== "fail" && (
        <div className="absolute z-10 inset-x-0 bottom-[14vh] flex justify-center pointer-events-none">
          <div
            className="px-3 py-1.5 font-mono text-[12px] tracking-[0.35em] uppercase bg-black/70 backdrop-blur-sm border"
            style={{
              borderColor: verifyState === "pass" ? "#5fff8a" : RETICLE_COLOR,
              color: verifyState === "pass" ? "#5fff8a" : RETICLE_COLOR,
            }}
            data-testid="cc-reticle-label"
          >
            {verifyState === "waiting" && "Align security key"}
            {verifyState === "checking" && "Checking..."}
            {verifyState === "pass" && `✓ Verified · ${verifyRevealed}`}
            {verifyState === "unknown" && (verifyError || "No match")}
          </div>
        </div>
      )}

      {/* ═══ FRAUD DETECTED OVERLAY ═══
          Block-3 spec: solid high-contrast black banner across the
          defaced data fields (= the reticle area). Sits at z-30,
          above the video and reticle. Single state flip → next
          paint cycle = sub-300 ms guarantee. */}
      {phase === "live" && verifyState === "fail" && (
        <>
          {/* Dim the surrounding video to push focus onto the banner */}
          <div className="absolute inset-0 z-20 bg-black/70 pointer-events-none" />

          {/* Banner: positioned across where the reticle was */}
          <div
            className="absolute z-30 inset-x-0 top-1/2 -translate-y-1/2 px-4"
            data-testid="cc-fraud-banner"
            role="alert"
            aria-live="assertive"
          >
            <div className="mx-auto max-w-md bg-black border-y-[3px] border-red-600 px-5 py-6 text-center">
              {/* The artifact image (destructive visual state from Block 2)
                  burns behind the banner text — proof of tampering */}
              {artifactPng && (
                <img
                  src={`data:image/png;base64,${artifactPng}`}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover opacity-30 mix-blend-screen pointer-events-none"
                  aria-hidden
                />
              )}
              <div className="relative">
                <div className="font-mono text-[10px] tracking-[0.45em] uppercase text-red-400">
                  Security alert
                </div>
                <div className="mt-2 font-display text-2xl sm:text-3xl text-white leading-tight">
                  ⚠️ FRAUD DETECTED
                </div>
                <div className="my-2 font-mono text-white/60 text-xs tracking-widest">
                  /
                </div>
                <div className="font-display text-lg sm:text-xl text-red-400 leading-tight">
                  DO NOT TRUST THIS DOCUMENT
                </div>
                <div className="mt-4 font-mono text-[10px] tracking-[0.3em] uppercase text-white/50">
                  doc · {docId} · {latencyMs}ms
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-center">
              <button
                onClick={resumeChecking}
                className="pointer-events-auto px-4 py-2 font-mono text-[10px] tracking-[0.3em] uppercase border border-white/40 text-white/80 bg-black/70"
                data-testid="cc-retry-after-fraud-btn"
              >
                Scan again
              </button>
            </div>
          </div>
        </>
      )}

      {/* Idle / error full-screen panels */}
      {phase !== "live" && (
        <div className="absolute inset-0 z-20 flex items-center justify-center px-6">
          <div className="max-w-sm w-full text-center">
            {phase === "idle" && (
              <>
                <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-white/60">Curb Checkpoint</div>
                <h1 className="font-display text-3xl mt-3" style={{ color: RETICLE_COLOR }}>
                  Live capture
                </h1>
                <p className="mt-3 text-sm text-white/70 leading-relaxed">
                  Enter the document ID, then point the camera at the security key. The system verifies continuously — no taps required.
                </p>
                <label className="block mt-6 text-left">
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50">Document ID</div>
                  <input
                    type="text"
                    value={docId}
                    onChange={(e) => setDocId(e.target.value.toUpperCase().trim())}
                    placeholder="e.g. AB12CD34"
                    className="mt-2 w-full bg-transparent border-b-2 border-[#FFCC00] py-2 text-xl font-mono tracking-widest text-white outline-none focus:border-white"
                    autoCapitalize="characters"
                    autoCorrect="off"
                    autoComplete="off"
                    spellCheck="false"
                    data-testid="cc-doc-id-input"
                  />
                </label>
                <button
                  onClick={startCamera}
                  disabled={!docId}
                  className="mt-6 inline-flex items-center justify-center w-full py-4 font-mono text-sm tracking-[0.3em] uppercase bg-[#FFCC00] text-black disabled:opacity-40"
                  data-testid="cc-start-btn"
                >
                  Begin capture
                </button>

                {/* Single-device demo path: skip the camera, upload a saved Share A image directly */}
                <label
                  className={`mt-3 inline-flex items-center justify-center w-full py-3 font-mono text-[10px] tracking-[0.3em] uppercase border border-white/40 ${docId ? "text-white cursor-pointer" : "text-white/30 cursor-not-allowed"}`}
                  data-testid="cc-upload-label"
                >
                  Or upload a Share A image
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={!docId}
                    onChange={(e) => verifyUploadedFile(e.target.files?.[0])}
                    data-testid="cc-upload-input"
                  />
                </label>
                <p className="mt-4 text-[10px] text-white/40 leading-relaxed">
                  Camera permission required. Frames are auto-verified in the background — nothing else stored on this device.
                </p>
              </>
            )}
            {phase === "starting" && (
              <div className="font-mono text-sm tracking-widest uppercase text-white/80">
                Initialising sensor…
              </div>
            )}
            {phase === "error" && (
              <>
                <h1 className="font-display text-3xl text-red-400">Capture failed</h1>
                <p className="mt-3 text-sm text-white/80 leading-relaxed">
                  {errorMsg}
                </p>
                <button
                  onClick={() => { setPhase("idle"); setErrorMsg(""); }}
                  className="mt-6 inline-flex items-center justify-center w-full py-3 font-mono text-sm tracking-[0.3em] uppercase border border-[#FFCC00] text-[#FFCC00]"
                  data-testid="cc-retry-btn"
                >
                  Back
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* End session button */}
      {phase === "live" && (
        <div className="absolute bottom-4 inset-x-0 z-20 flex justify-center pointer-events-none">
          <button
            onClick={stopCamera}
            className="pointer-events-auto px-4 py-2 font-mono text-[10px] tracking-[0.3em] uppercase border border-white/30 text-white/80 bg-black/50 backdrop-blur-sm"
            data-testid="cc-stop-btn"
          >
            End session
          </button>
        </div>
      )}
    </div>
  );
}
